import { FC, MouseEvent, useState } from 'react';
import styled from 'styled-components';

import Button from '@/components/Button';
import Checkbox from '@/components/Checkbox';
import { Input, RadioCircle, RadioInput, Textarea } from '@/components/Forms';
import ShowHideButton from '@/components/ShowHideButton';
import Text from '@/components/Text';
import { getOptimalFontSize } from '@/utils/coverImages/textDistribution';

import SimpleUploadedImageRow from '../SimpleUploadedImageRow';
import UploadSingleImage from '../UploadSingleImage';
import {
  Box,
  ButtonHolder,
  Header,
  HeaderButtons,
  Heading,
  ImageData,
  LabelText,
  ResetButton,
  UploadedImages,
} from '../VariationOne';
import { ImageGrid, TextAreaBox, TextParametersBox } from '../VariationTwo';
import {
  ActionBox,
  ActionTitle,
  StyledRadiosLabel,
} from '../shared/styledComponents';
import GeneratedImages from './GeneratedImages';
import VariationsStyles from './VariationsStyles';

export const CHANNEL_NAME_COLORS = ['black', 'blue'];

export const CHANNEL_IMAGE_OPTIONS = ['custom', 'reddit'];

export interface ImageVariation {
  leftText: string;
  leftFontSize?: number;
  leftBrakes: boolean;
  leftAuthorName: string;
  leftAuthorImage?: string;
  leftChannelNameColor: string;
  leftChannelImageOption: string;
  rightText: string;
  authorImage?: string;
  authorName: string;
  channelNameColor: string;
  brakes: boolean;
  fontSize?: number;
  channelImageOption: string;
}

const StyledTextArea = styled(Textarea)`
  height: 86px;
`;

const NameTextArea = styled(Textarea)`
  height: 46px;
`;

const UploadAuthorImages = styled(UploadSingleImage)`
  height: 98px;
`;

const ChannelImageHolder = styled.div`
  margin-top: 16px;
`;

const ChannelNameHolder = styled.div`
  margin-top: 16px;
`;

const HeadingsHolder = styled.div`
  flex: 1;
`;

getOptimalFontSize('some random text', {
  maxWidth: 530,
  maxHeight: 560,
  fontFamily: 'Gothic',
});

interface Props {
  inArticle?: boolean;
}

const VariationCommentComment: FC<Props> = ({ inArticle }) => {
  const [leftText, setLeftText] = useState('');
  const [leftFontSize, setLeftFontSize] = useState('');
  const [leftBrakes, setLeftBrakes] = useState(false);
  const [leftNameColor, setLeftNameColor] = useState('black');
  const [leftChannelImageOption, setLeftChannelImageOption] =
    useState('custom');
  const [leftAuthorName, setLeftAuthorName] = useState('');
  const [leftAuthorImages, setLeftAuthorImages] = useState<ImageData[]>([]);

  const [showContent, setShowContent] = useState(true);
  const [brakes, setBrakes] = useState(false);
  const [text, setText] = useState('');
  const [fontSize, setFontSize] = useState('');

  const [variations, setVariations] = useState<ImageVariation[]>([]);
  const [authorImages, setAuthorImages] = useState<ImageData[]>([]);
  const [channelImageOption, setChannelImageOption] = useState('custom');

  const [authorName, setAuthorName] = useState('');
  const [nameColor, setNameColor] = useState('black');
  const [images, setImages] = useState<string[]>();

  const onReset = () => {
    setVariations([]);
    setImages([]);
    setAuthorImages([]);
    setBrakes(false);
    setText('');
    setAuthorName('');
    setFontSize('');
    setNameColor('black');
    setChannelImageOption('custom');
    setLeftText('');
    setLeftFontSize('');
    setLeftBrakes(false);
    setLeftAuthorName('');
    setLeftAuthorImages([]);
    setLeftNameColor('black');
    setLeftChannelImageOption('custom');
  };

  const generateVariations = async (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();

    const newVariations = () => [
      {
        leftText: leftText,
        leftFontSize: parseInt(leftFontSize),
        leftBrakes: leftBrakes,
        leftAuthorName: leftAuthorName,
        leftAuthorImage: !!leftAuthorImages.length
          ? leftAuthorImages[0]?.croppedImage ??
            URL.createObjectURL(leftAuthorImages[0].image)
          : undefined,
        leftChannelNameColor: leftNameColor,
        leftChannelImageOption: leftChannelImageOption,
        rightText: text,
        brakes: brakes,
        fontSize: parseInt(fontSize),
        authorName: authorName,
        authorImage: !!authorImages.length
          ? authorImages[0]?.croppedImage ??
            URL.createObjectURL(authorImages[0].image)
          : undefined,
        channelNameColor: nameColor,
        channelImageOption: channelImageOption,
      },
    ];

    setVariations(newVariations);
  };

  const renderAuthorImages = authorImages.map((image, index) => (
        <SimpleUploadedImageRow
          key={`${index}-${image.image.name}-${image.image.lastModified}`}
          imageIndex={index}
          image={image}
          setImages={setAuthorImages}
        />
      ));

  const renderLeftAuthorImages = leftAuthorImages.map((image, index) => (
        <SimpleUploadedImageRow
          key={`${index}-${image.image.name}-${image.image.lastModified}`}
          imageIndex={index}
          image={image}
          setImages={setLeftAuthorImages}
        />
      ));

  const isResetDisabled =
    !fontSize &&
    !text &&
    !brakes &&
    !authorImages.length &&
    !authorName &&
    nameColor === 'black' &&
    channelImageOption === 'custom' &&
    !leftText &&
    !leftFontSize &&
    !leftBrakes &&
    !leftAuthorImages.length &&
    !leftAuthorName &&
    leftNameColor === 'black' &&
    leftChannelImageOption === 'custom';

  return (
    <>
      <Box>
        <Header>
          <HeadingsHolder>
            <Heading $type="button">Comment + Comment</Heading>
            <Text $type="smallHeadline" $color="gray">
              Attach two comments the necessary parameters. Additionally,
              include the author's image and name
            </Text>
          </HeadingsHolder>
          <HeaderButtons>
            <ResetButton
              color="red"
              disabled={isResetDisabled}
              onClick={onReset}
            >
              Reset
            </ResetButton>
            <ShowHideButton
              showContent={showContent}
              onClick={() => setShowContent((prev) => !prev)}
            />
          </HeaderButtons>
        </Header>
        {showContent && (
          <ImageGrid>
            <TextParametersBox>
              <TextAreaBox>
                <div>
                  <LabelText $type="smallHeadline">Text</LabelText>
                  <StyledTextArea
                    onChange={(e) => setLeftText(e.target.value)}
                    value={leftText}
                    placeholder="Add text"
                  ></StyledTextArea>
                </div>
                <div>
                  <LabelText $type="smallHeadline">Font Size</LabelText>
                  <Input
                    value={leftFontSize}
                    onChange={(e) => setLeftFontSize(e.target.value)}
                    placeholder="Font Size (px)"
                    type="number"
                    min="0"
                  />
                </div>
                <div>
                  <ActionTitle $type="smallHeadline">
                    Text Parameters
                  </ActionTitle>
                  <ActionBox>
                    <Checkbox
                      checked={leftBrakes}
                      onChange={() => setLeftBrakes((prev) => !prev)}
                    >
                      Custom brakes
                    </Checkbox>
                  </ActionBox>
                </div>
                <div>
                  <LabelText $type="smallHeadline">Channel Image</LabelText>
                  <ActionBox>
                    {CHANNEL_IMAGE_OPTIONS.map((option) => (
                      <StyledRadiosLabel key={option}>
                        {option}
                        <RadioInput
                          type="radio"
                          name="lef_channel_image"
                          checked={leftChannelImageOption === option}
                          onChange={() => setLeftChannelImageOption(option)}
                        />
                        <RadioCircle />
                      </StyledRadiosLabel>
                    ))}
                  </ActionBox>
                  {leftChannelImageOption === 'custom' && (
                    <ChannelImageHolder>
                      <LabelText $type="smallHeadline">
                        Add Channel Image
                      </LabelText>
                      {!!leftAuthorImages?.length ? (
                        <UploadedImages>
                          {renderLeftAuthorImages}
                        </UploadedImages>
                      ) : (
                        <UploadAuthorImages
                          coverIndex={2}
                          setImages={setLeftAuthorImages}
                        />
                      )}
                    </ChannelImageHolder>
                  )}
                </div>
                <div>
                  <LabelText $type="smallHeadline">
                    Channel Name Color
                  </LabelText>
                  <ActionBox>
                    {CHANNEL_NAME_COLORS.map((color) => (
                      <StyledRadiosLabel key={color}>
                        {color}
                        <RadioInput
                          type="radio"
                          name="left_name_color"
                          checked={leftNameColor === color}
                          onChange={() => setLeftNameColor(color)}
                        />
                        <RadioCircle />
                      </StyledRadiosLabel>
                    ))}
                  </ActionBox>
                  <ChannelNameHolder>
                    <LabelText $type="smallHeadline">Channel Name</LabelText>
                    <NameTextArea
                      placeholder="Add Name"
                      onChange={(e) => setLeftAuthorName(e.target.value)}
                      value={leftAuthorName}
                    ></NameTextArea>
                  </ChannelNameHolder>
                </div>
              </TextAreaBox>
            </TextParametersBox>
            <TextParametersBox>
              <TextAreaBox>
                <div>
                  <LabelText $type="smallHeadline">Text</LabelText>
                  <StyledTextArea
                    onChange={(e) => setText(e.target.value)}
                    value={text}
                    placeholder="Add text"
                  ></StyledTextArea>
                </div>
                <div>
                  <LabelText $type="smallHeadline">Font Size</LabelText>
                  <Input
                    value={fontSize}
                    onChange={(e) => setFontSize(e.target.value)}
                    placeholder="Font Size (px)"
                    type="number"
                    min="0"
                  />
                </div>
                <div>
                  <ActionTitle $type="smallHeadline">
                    Text Parameters
                  </ActionTitle>
                  <ActionBox>
                    <Checkbox
                      checked={brakes}
                      onChange={() => setBrakes((prev) => !prev)}
                    >
                      Custom brakes
                    </Checkbox>
                  </ActionBox>
                </div>
                <div>
                  <LabelText $type="smallHeadline">Channel Image</LabelText>
                  <ActionBox>
                    {CHANNEL_IMAGE_OPTIONS.map((option) => (
                      <StyledRadiosLabel key={option}>
                        {option}
                        <RadioInput
                          type="radio"
                          name="channel_image"
                          checked={channelImageOption === option}
                          onChange={() => setChannelImageOption(option)}
                        />
                        <RadioCircle />
                      </StyledRadiosLabel>
                    ))}
                  </ActionBox>
                  {channelImageOption === 'custom' && (
                    <ChannelImageHolder>
                      <LabelText $type="smallHeadline">
                        Add Channel Image
                      </LabelText>
                      {!!authorImages?.length ? (
                        <UploadedImages>{renderAuthorImages}</UploadedImages>
                      ) : (
                        <UploadAuthorImages
                          coverIndex={2}
                          setImages={setAuthorImages}
                        />
                      )}
                    </ChannelImageHolder>
                  )}
                </div>
                <div>
                  <LabelText $type="smallHeadline">
                    Channel Name Color
                  </LabelText>
                  <ActionBox>
                    {CHANNEL_NAME_COLORS.map((color) => (
                      <StyledRadiosLabel key={color}>
                        {color}
                        <RadioInput
                          type="radio"
                          name="name_color"
                          checked={nameColor === color}
                          onChange={() => setNameColor(color)}
                        />
                        <RadioCircle />
                      </StyledRadiosLabel>
                    ))}
                  </ActionBox>
                  <ChannelNameHolder>
                    <LabelText $type="smallHeadline">Channel Name</LabelText>
                    <NameTextArea
                      placeholder="Add Name"
                      onChange={(e) => setAuthorName(e.target.value)}
                      value={authorName}
                    ></NameTextArea>
                  </ChannelNameHolder>
                </div>
              </TextAreaBox>
            </TextParametersBox>
          </ImageGrid>
        )}
      </Box>
      <VariationsStyles />
      {showContent && (
        <ButtonHolder>
          <Button onClick={generateVariations}>
            {!!variations?.length ? 'Regenerate' : 'Generate'}
          </Button>
        </ButtonHolder>
      )}
      <GeneratedImages
        variations={variations}
        images={images}
        setImages={setImages}
        inArticle={inArticle}
        onReset={onReset}
      />
    </>
  );
};

export default VariationCommentComment;
